import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import "datatables.net-bs4";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Select from "react-select";
import { useCompanyOptions } from "../../utils/company";
import { useProviderOptions } from "../../store/actions/providerActions";
import { useBookings } from "../../utils/booking";
import { MdEditCalendar } from "react-icons/md";
import $ from "jquery";
import { useDrivers } from "../../utils/driver";
import { UserContext } from "../../App";
import { verifyRight } from "../../utils/users";
import { NavItem, NavTabs } from "../layout/Nav";
import { Link } from "react-router-dom";
import { exportBookingsToCSV } from "../../utils/csvExport";
import { FiDownload } from "react-icons/fi";

const customStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: "200px",
  }),
};

export default function TurnoverContent() {
  const { userInfos } = useContext(UserContext);
  const companyOptions = useCompanyOptions(true);
  const providerOptions = useProviderOptions(true);
  const allDrivers = useDrivers();

  const [companyFilter, setCompanyFilter] = useState(null);
  const [driverFilter, setDriverFilter] = useState(null);
  const [providerFilter, setProviderFilter] = useState(null);

  const datatable = useRef(null);

  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("month"),
    endDate: moment().endOf("month"),
  });

  const { bookings, loaded } = useBookings(
    dateRange.startDate,
    dateRange.endDate,
    true
  );
  const [filteredBookings, setFilteredBookings] = useState(bookings);

  const driverOptions = useMemo(() => {
    if (
      !companyFilter ||
      !companyFilter.id ||
      !allDrivers?.drivers ||
      allDrivers?.drivers?.length <= 0
    )
      return null;
    let results = [
      {
        label: "Tous les chauffeurs",
        value: null,
        data: null,
      },
    ];

    results.push(
      ...allDrivers.drivers
        .filter((driver) => driver?.company?.id === companyFilter.id)
        .map((driver) => ({
          label: driver.name,
          value: driver.id,
          data: driver,
        }))
    );

    return results;
  }, [companyFilter, allDrivers]);

  useEffect(() => {
    let res = bookings;

    if (companyFilter) {
      res = res.filter(
        (booking) => booking?.driver?.company?.id === companyFilter.id
      );
    }

    if (driverFilter) {
      res = res.filter((booking) => booking?.driver?.id === driverFilter.id);
    }

    if (providerFilter) {
      res = res.filter(
        (booking) => booking?.provider?.id === providerFilter.id
      );
    }

    // region trick to delete jquery datatable dom before React Shadow DOM update
    if (datatable.current) {
      datatable.current.destroy();
      datatable.current = null;
    }
    // endregion

    setFilteredBookings(res);
  }, [bookings, companyFilter, providerFilter, driverFilter]);

  useEffect(() => {
    if (!loaded) return;

    if (datatable.current) {
      datatable.current.destroy();
      datatable.current = null;
    }

    window.setTimeout(() => {
      datatable.current = $("#datatable").DataTable({
        retrieve: true,
        order: [[0, "asc"]],
        pageLength: 25,
        language: {
          lengthMenu: "Afficher _MENU_ lignes",
          entries: {
            _: "courses",
            1: "course",
          },
          info: "Affichage de _START_ à _END_ _ENTRIES-TOTAL_ sur un total de _TOTAL_ _ENTRIES-TOTAL_",
        },
      });
    }, 1);
  }, [filteredBookings, loaded]);

  function handleCalendar(event, picker) {
    setDateRange({
      startDate: picker.startDate.startOf("day"),
      endDate: picker.endDate.endOf("day"),
    });

    // region trick to delete jquery datatable dom before React Shadow DOM update
    if (datatable.current) {
      datatable.current.destroy();
      datatable.current = null;
    }
    // endregion
  }

  const totalTurnover = useMemo(() => {
    let res = 0;
    if (loaded) {
      res = filteredBookings.reduce(
        (acc, booking) =>
          acc +
          (isNaN(parseFloat(booking.amount)) ? 0 : parseFloat(booking.amount)),
        0
      );
    }
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(res);
  }, [filteredBookings, loaded]);

  const totalDriverCost = useMemo(() => {
    let res = 0;
    if (loaded) {
      res = filteredBookings.reduce(
        (acc, booking) =>
          acc +
          (isNaN(parseFloat(booking.driver_price))
            ? 0
            : parseFloat(booking.driver_price)),
        0
      );
    }
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(res);
  }, [filteredBookings, loaded]);

  function handleCompanyChange(company) {
    setDriverFilter(null);
    setCompanyFilter(company.data);
  }

  function handleDriverChange(driver) {
    setDriverFilter(driver.data);
  }

  function handleProviderChange(provider) {
    setProviderFilter(provider.data);
  }

  function handleExportCSV() {
    const filters = {
      company: companyFilter,
      driver: driverFilter,
      provider: providerFilter,
      dateRange: {
        startDate: dateRange.startDate.format("DD/MM/YYYY"),
        endDate: dateRange.endDate.format("DD/MM/YYYY")
      }
    };
    
    exportBookingsToCSV(filteredBookings, filters);
  }

  const datatableContent = useMemo(() => {
    return (
      <tbody>
        {filteredBookings.map((booking) => (
          <tr key={booking.id}>
            <td data-order={booking.id}>{booking.id}</td>
            <td data-order={booking.pickup_date.sortDate}>
              {booking.pickup_date.date}
            </td>
            <td>{booking.provider?.name ?? ""}</td>
            <td>{booking.firstname + " " + booking.lastname}</td>
            <td>{booking.pickup_address}</td>
            <td>{booking.dropoff_address}</td>
            <td>{booking.driver?.company?.name}</td>
            <td>{booking.driver?.name}</td>
            <td>{booking.driver_price}</td>
          </tr>
        ))}
      </tbody>
    );
  }, [filteredBookings]);

  if (!userInfos) return null;

  return (
    <div className="body-container" id="kt_content">
      <div className="title-container">
        <NavTabs className="titles">
          <NavItem className="active">
            <Link to="/turnover/">Turnover</Link>
          </NavItem>
          {verifyRight(userInfos, "invoice", "read") && (
            <NavItem>
              <Link to="/invoice-stats/">Stats factures</Link>
            </NavItem>
          )}
        </NavTabs>
        <div className="header-toolbar d-flex align-items-center gap-3">
          {(companyFilter || providerFilter) && (
            <button
              onClick={handleExportCSV}
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={!loaded || filteredBookings.length === 0}
              title="Exporter les données en CSV"
            >
              <FiDownload size={16} />
              Exporter CSV
            </button>
          )}
          <DateRangePicker
            initialSettings={{
              startDate: dateRange.startDate.format("DD/MM/YYYY"),
              endDate: dateRange.endDate.format("DD/MM/YYYY"),
            }}
            onApply={handleCalendar}
          >
            <button
              type="button"
              className="btn kt-subheader__btn-daterange"
              data-toggle="kt-tooltip"
              title="Select dashboard daterange"
              data-placement="left"
            >
              <span
                className="kt-subheader__btn-daterange-date mr-2"
                id="kt_dashboard_daterangepicker_date"
              >
                {dateRange.startDate.format("DD/MM/YYYY") +
                  " - " +
                  dateRange.endDate.format("DD/MM/YYYY")}
              </span>
              <MdEditCalendar size={24} />
            </button>
          </DateRangePicker>
        </div>
      </div>

      <div>
        <div className="d-flex justify-content-between flex-wrap mb-4">
          <div className="price_container">
            <h4 className="">
              Chiffre d'affaire de{" "}
              {companyFilter?.name || "Toutes les entreprises"} :{" "}
              {totalTurnover}
            </h4>
            <h4 className="">Coût chauffeurs : {totalDriverCost}</h4>
          </div>
          <div
            className="d-flex align-items-start flex-wrap gap-3"
            style={{ width: "420px", maxWidth: "100%" }}
          >
            <div className="d-flex flex-column gap-2">
              {companyOptions.length > 0 && (
                <Select
                  placeholder="Selectionner Entreprise"
                  defaultValue={companyOptions[0]}
                  styles={customStyles}
                  options={companyOptions}
                  onChange={handleCompanyChange}
                  className="react-select-container"
                />
              )}
              {driverOptions && driverOptions.length > 0 && (
                <Select
                  placeholder="Sélectionner Chauffeur"
                  defaultValue={driverOptions[0]}
                  styles={customStyles}
                  options={driverOptions}
                  onChange={handleDriverChange}
                  className="react-select-container"
                />
              )}
            </div>
            {providerOptions.length > 0 && (
              <Select
                placeholder="Select Provider"
                defaultValue={providerOptions[0]}
                styles={customStyles}
                options={providerOptions}
                onChange={handleProviderChange}
                className="react-select-container"
              />
            )}
          </div>
        </div>
      </div>
      <div className="kt-portlet__body" style={{ overflowX: "scroll" }}>
        <table
          className="table table-striped- table-bordered table-hover table-checkable"
          id="datatable"
        >
          <thead>
            <tr>
              <th style={{ width: "6%" }}>#</th>
              <th style={{ width: "9%" }}>Date</th>
              <th style={{ width: "8%" }}>Client</th>
              <th style={{ width: "8%" }}>Passager</th>
              <th style={{ width: "21%" }}>Départ</th>
              <th style={{ width: "21%" }}>Arrivée</th>
              <th style={{ width: "10%" }}>Compagnie</th>
              <th style={{ width: "8%" }}>Chauffeur</th>
              <th style={{ width: "7%" }}>Prix (chauffeur)</th>
              {/*<th style={{ width: "12%", textAlign: "center" }}>Status</th>*/}
            </tr>
          </thead>
          {datatableContent}
        </table>
      </div>
    </div>
  );
}
